import { useQueryClient } from "@tanstack/react-query";
import { Camera } from "lucide-react";
import { useRef, useState } from "react";
import type { ImportJobDto } from "../../../shared/api-types";
import { importApi, keys } from "../../api/queries";
import { useDocumentPicker } from "../../components/UploadZone";
import { Alert, ICON } from "../../components/ui";
import { importFileError } from "../../lib/importFiles";
import { includedPages, isCancelled, usePagePreparation } from "../../lib/prepare/usePagePreparation";

// Pages whose readings stayed uncertain (owner, 2026-10-22: a page with many errors asks for a new photo). The new
// photo is prepared like any other (quality checks, zoomed crops) and replaces that page only; the draft keeps
// what the restaurant already edited.

export function RetakePages({ rid, job }: { rid: string; job: ImportJobDto }) {
  const qc = useQueryClient();
  const prep = usePagePreparation();
  const target = useRef<{ fileId: string; page: number } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const pages = job.files.filter((f) => f.quality?.retake);

  const picker = useDocumentPicker(async ([photo]) => {
    const t = target.current;
    if (!photo || !t) return;
    const invalid = importFileError(photo);
    if (invalid) {
      setMessage(invalid);
      return;
    }
    setMessage("");
    setBusy(t.fileId);
    try {
      const ready = await prep.start([photo], "camera");
      if (ready.phase !== "ready" || !includedPages(ready).length) {
        setMessage(ready.error || "Fotografia nu a putut fi pregătită. Încearcă din nou.");
        return;
      }
      await prep.upload(importApi(rid), job.id, t);
      prep.clear();
      await qc.invalidateQueries({ queryKey: keys.imports(rid) });
    } catch (e) {
      if (!isCancelled(e)) setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  });

  if (!pages.length) return null;
  return (
    <Alert tone="warning" title="Unele pagini au fost greu de citit">
      <div className="stack" style={{ gap: 10 }}>
        <span>
          Citirile acestor pagini nu s-au confirmat între ele, așa că multe produse au rămas de verificat. O
          fotografie nouă, mai aproape, dreaptă și fără reflexii, se citește mult mai bine decât o verificare
          lungă.
        </span>
        {picker.inputs}
        {pages.map((f) => {
          const page = f.page ?? f.position + 1;
          return (
            <div key={f.id} className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <span>
                Pagina {page}: {f.quality?.uncertain} din {f.quality?.products} produse nesigure
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy !== null}
                onClick={() => {
                  target.current = { fileId: f.id, page };
                  picker.open("camera");
                }}
              >
                <Camera {...ICON} aria-hidden="true" />
                {busy === f.id ? "Se trimite fotografia…" : `Refotografiază pagina ${page}`}
              </button>
            </div>
          );
        })}
        {message && (
          <span role="alert" className="field-error">
            {message}
          </span>
        )}
      </div>
    </Alert>
  );
}
